import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CenovaPonuka,
  CenovaPonukaSearchQuery,
  CenovaPonuka as CenovaPonukaType,
} from "../../../ts/CP";
import CenovaPonukaComponent from "./CenovaPonuka/CenovaPonukaComponent";
import "./CenovePonuky.scss";
import Header from "./Header";

import { useNavigate, useParams } from "react-router";
import { cenovaPonukaModel } from "../../../api/models/cenovaPonukaModel";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { getCPStateLabel } from "../../../utils";
import Loading from "../../ui/Loading/Loading";
import SeznamPonuk from "./SeznamPonuk";

const CenovePonukyContext = React.createContext<
  | [
      value: CenovaPonukaType | undefined,
      setValue: (
        cenovaPonuka: CenovaPonukaType,
        success?: (state: boolean) => void
      ) => void,
      setIndex: (value: number) => void,
      addNewCP: (
        cp: CenovaPonukaType,
        success: (state: boolean, id: string) => void
      ) => void
    ]
  | []
>([]);

const EditableContext = React.createContext<
  [
    editable: boolean,
    setEditable: (editable: boolean) => void,
    listShown: boolean,
    setListShown: (state: boolean) => void,
    newCP: boolean,
    setNewCP: (editable: boolean) => void
  ]
>([false, () => null, false, () => null, false, () => null]);

export const useCenovaPonuka = () => {
  return React.useContext(CenovePonukyContext);
};

export const useEditable = () => {
  return React.useContext(EditableContext);
};

interface Props {}

const CenovePonuky: React.FC<Props> = ({}: Props) => {
  const { id, kontakt } = useParams();
  const [index, setIndex] = useState<number>(0);
  const [editable, setEditable] = useState(false);
  const [listShown, setListShown] = useState(false);
  const [newCP, setNewCP] = useState(false);

  const [query, setQuery] = useState<CenovaPonukaSearchQuery | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    if (kontakt && kontakt.length === 24) {
      setListShown(true);
      setQuery({
        cislo_ponuky: "",
        objednavatel: "",
        prijimatel: "",
        kontakt_id: kontakt,
      });
    }
  }, [kontakt]);

  const navigate = useNavigate();

  const { call } = useQueryWithPopupErrorHandling();
  const [data, setData] = useState<CenovaPonuka | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCp, setIsLoadingCp] = useState(false);
  const [pageSize] = useState(1);
  const fetchedWithId = useRef<string | null>(null);

  const getCenovaPonuka = async (id?: string, preventNavigation?: boolean) => {
    await call(
      () =>
        cenovaPonukaModel.getCenovaPonukaWithIndex({
          id,
          offset: index,
          limit: pageSize.toString(),
        }),
      "Zobrazenie cenovej ponuky nebolo úspešné",
      null,
      ({ cenovaPonuka, totalCount, index: newIndex }) => {
        setData(cenovaPonuka);
        setTotalCount(totalCount);

        if (index !== newIndex) {
          setIndex(newIndex);
        }

        if (preventNavigation) {
          return;
        }

        const url = `/cenove-ponuky/${cenovaPonuka._id}`;
        navigate(url, { replace: true, flushSync: true });
      },
      () => {
        fetchedWithId.current = null;
        navigate("/cenove-ponuky");
      },
      true
    );
    setIsLoading(false);
  };

  useEffect(() => {
    if (id && !fetchedWithId.current) {
      fetchedWithId.current = id;
      getCenovaPonuka(id);
    }
  }, [id]);

  useEffect(() => {
    getCenovaPonuka();
  }, [index, pageSize]);

  const handleSearch = (query: CenovaPonukaSearchQuery) => {
    setQuery(query);
    setListShown(true);
  };

  const editCp = useCallback(
    (cenovaPonuka: CenovaPonukaType) => {
      call(
        () => cenovaPonukaModel.editCenovaPonuka({ cp: cenovaPonuka }),
        "Úprava cenovej ponuky nebola úspešná",
        "Úprava cenovej ponuky bola úspešná",
        () => {
          getCenovaPonuka(id, true);
          setEditable(false);
        }
      );
    },
    [id, index, pageSize]
  );

  useEffect(() => {
    const cb = () => {
      fetchedWithId.current = null;
    };

    window.addEventListener("popstate", cb);

    return () => {
      window.removeEventListener("popstate", cb);
    };
  }, []);

  const createCp = useCallback((cenovaPonuka: CenovaPonukaType) => {
    call(
      () => cenovaPonukaModel.createCenovaPonuka({ cp: cenovaPonuka }),
      "Pridanie cenovej ponuky nebolo úspešné",
      "Pridanie cenovej ponuky bolo úspešné",
      (newCp) => {
        fetchedWithId.current = null;
        navigate(`/cenove-ponuky/${newCp._id}`);
        setNewCP(false);
      }
    );
  }, []);

  if (isLoading) {
    return <Loading text="Získávanie cenových ponuk zo servera" />;
  }

  return (
    <CenovePonukyContext.Provider
      value={[
        data,
        (cenovaPonuka: CenovaPonukaType) => editCp(cenovaPonuka),
        (value: number) =>
          setIndex(value > totalCount - 1 ? index : value < 0 ? index : value),
        (_cp: CenovaPonukaType) => createCp(_cp),
      ]}
    >
      <div className="cenove-ponuky">
        <div className="title-wrapper">
          {isLoading ? null : <div className="title">Cenová ponuka</div>}
          {data && getCPStateLabel(data.state) ? (
            <div className="state">
              Stav: <span>{getCPStateLabel(data.state)}</span>
            </div>
          ) : null}
        </div>
        <EditableContext.Provider
          value={[
            editable,
            setEditable,
            listShown,
            setListShown,
            newCP,
            setNewCP,
          ]}
        >
          <Header
            length={totalCount}
            index={index}
            handleSearch={handleSearch}
            refreshCp={() => getCenovaPonuka()}
            refreshFetchId={() => (fetchedWithId.current = null)}
            isLoading={isLoadingCp}
          />
          {!isLoading && totalCount === 0 && !newCP ? (
            <div className="cp-not-found">Žiadne cenové ponuky sa nenašli</div>
          ) : (
            <>
              <SeznamPonuk
                onSelectRow={() => {
                  fetchedWithId.current = null;
                  setListShown(false);
                  window.scrollTo({ top: 0 });
                }}
                shown={listShown}
                query={query}
                setQuery={setQuery}
              />
              <CenovaPonukaComponent
                shown={!listShown}
                refreshFetchId={() => (fetchedWithId.current = null)}
              />
            </>
          )}
        </EditableContext.Provider>
      </div>
    </CenovePonukyContext.Provider>
  );
};

export default CenovePonuky;
